/* Personal records: Epley e1RM, session bests, all-time bests, PR events and
 * the records table. Pure functions over state.logs; the Python twin is
 * src/fitlogic/records.py and both are pinned by vectors/records.json.
 *
 * A set counts when it is done with weight > 0 and reps > 0. Old sets
 * ({ weight, reps, done } or a bare { done: true }) need nothing else.
 * Assisted exercises (exerciseMeta[name].assisted, else /assisted/i on the
 * name) log the counterweight, so lower is better and they get no e1RM. */

const DATE_RE = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
const TYPE_ORDER = ['weight', 'e1rm', 'reps'];

function num(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function optNum(opts, key, fallback) {
  const v = opts ? opts[key] : undefined;
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/* Math.round is half-up; the Python twin uses floor(x * f + 0.5) / f. The
 * `|| 0` turns -0 into 0 so results compare equal across languages. */
function roundTo(x, f) {
  return Math.round(x * f) / f || 0;
}

function round1(x) { return roundTo(x, 10); }

/* Days since the epoch for a real YYYY-MM-DD key, else null (2026-02-30 too). */
function dayNumber(key) {
  if (typeof key !== 'string' || !DATE_RE.test(key)) return null;
  const [y, m, d] = key.split('-').map(Number);
  const back = new Date(Date.UTC(y, m - 1, d));
  if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
  return Math.round(back.getTime() / 86400000);
}

function dateKeys(logs) {
  if (!logs || typeof logs !== 'object') return [];
  return Object.keys(logs).filter(k => dayNumber(k) !== null).sort();
}

function setsOf(logs, date, name) {
  const day = logs[date];
  return day && typeof day === 'object' && !Array.isArray(day) ? day[name] : undefined;
}

/**
 * Epley estimated 1RM, weight x (1 + reps / 30). fitness_tools.epley calls
 * the Python twin, so the MCP numbers get the same cap and rounding.
 * @param {number} weight kg, > 0
 * @param {number} reps 1-12; above 12 the estimate is too loose to use
 * @returns {number|null} rounded to 0.1 kg; reps 1 returns the weight itself;
 *   null for reps > 12, reps < 1, weight <= 0 or a non-number
 */
export function epley(weight, reps) {
  if (typeof weight !== 'number' || typeof reps !== 'number') return null;
  if (!Number.isFinite(weight) || !Number.isFinite(reps)) return null;
  if (weight <= 0 || reps < 1 || reps > 12) return null;
  if (reps === 1) return round1(weight);
  return round1(weight * (1 + reps / 30));
}

/**
 * Whether an exercise logs a counterweight (lower = better).
 * @param {string} name exercise name as logged
 * @param {object} [exerciseMeta] state.exerciseMeta; an explicit boolean
 *   `assisted` wins over the /assisted/i name rule
 * @returns {boolean}
 */
export function isAssisted(name, exerciseMeta) {
  const meta = exerciseMeta && typeof exerciseMeta === 'object' ? exerciseMeta[name] : null;
  if (meta && typeof meta === 'object' && typeof meta.assisted === 'boolean') return meta.assisted;
  return typeof name === 'string' && /assisted/i.test(name);
}

function resolveAssisted(name, opts) {
  if (opts && typeof opts.assisted === 'boolean') return opts.assisted;
  return isAssisted(name, opts && opts.exerciseMeta);
}

/**
 * The sets that count for records and trends: done, weight > 0, reps > 0.
 * @param {Array} sets logs[date][name]
 * @returns {Array<{weight:number, reps:number, rir:number|null, index:number}>}
 *   index is the position in the original array
 */
export function completedSets(sets) {
  const out = [];
  if (!Array.isArray(sets)) return out;
  sets.forEach((s, index) => {
    if (!s || typeof s !== 'object' || Array.isArray(s) || !s.done) return;
    const weight = num(s.weight);
    const reps = num(s.reps);
    if (!(weight > 0 && reps > 0)) return;
    const rir = typeof s.rir === 'number' && Number.isFinite(s.rir) ? s.rir : null;
    out.push({ weight, reps, rir, index });
  });
  return out;
}

function beats(a, b, assisted) {
  if (assisted) {
    if (a.weight !== b.weight) return a.weight < b.weight;
    return a.reps > b.reps;
  }
  // A set with an e1RM outranks one above 12 reps; those only win when
  // nothing in the session has an e1RM (e.g. calf raises at 20).
  if ((a.e1rm === null) !== (b.e1rm === null)) return a.e1rm !== null;
  if (a.e1rm !== null && a.e1rm !== b.e1rm) return a.e1rm > b.e1rm;
  if (a.weight !== b.weight) return a.weight > b.weight;
  return a.reps > b.reps;
}

/**
 * Best completed set of one session.
 * @param {Array} sets logs[date][name]
 * @param {{assisted?: boolean}} [opts] assisted: lowest weight wins, then most reps
 * @returns {{weight:number, reps:number, e1rm:number|null, index:number}|null}
 *   highest e1RM (ties: heavier, then more reps, then first); when no set has
 *   an e1RM, the heaviest set with e1rm null; null when nothing counts
 */
export function sessionBest(sets, opts = {}) {
  const assisted = !!(opts && opts.assisted);
  let best = null;
  for (const s of completedSets(sets)) {
    const cand = { weight: s.weight, reps: s.reps, e1rm: assisted ? null : epley(s.weight, s.reps), index: s.index };
    if (!best || beats(cand, best, assisted)) best = cand;
  }
  return best;
}

/**
 * All-time best, with the app's findExerciseAllTimeBest meaning: the
 * heaviest weight and the most reps at that weight (assisted: the lowest).
 * @param {object} logs state.logs
 * @param {string} name exercise name
 * @param {{beforeDate?: string, assisted?: boolean, exerciseMeta?: object}} [opts]
 *   beforeDate: only dates strictly before it
 * @returns {{weight:number, reps:number, bestE1rm:number|null, sessions:number}}
 *   weight and reps are 0 with no history; sessions = dates with a counted set
 */
export function allTimeBest(logs, name, opts = {}) {
  const assisted = resolveAssisted(name, opts);
  const before = opts && typeof opts.beforeDate === 'string' ? opts.beforeDate : null;
  let weight = 0;
  let reps = 0;
  let bestE1rm = null;
  let sessions = 0;
  for (const date of dateKeys(logs)) {
    if (before !== null && date >= before) break;
    const sets = completedSets(setsOf(logs, date, name));
    if (!sets.length) continue;
    sessions += 1;
    for (const s of sets) {
      if (!weight || (assisted ? s.weight < weight : s.weight > weight)) {
        weight = s.weight;
        reps = s.reps;
      } else if (s.weight === weight && s.reps > reps) {
        reps = s.reps;
      }
      if (!assisted) {
        const e = epley(s.weight, s.reps);
        if (e !== null && (bestE1rm === null || e > bestE1rm)) bestE1rm = e;
      }
    }
  }
  return { weight, reps, bestE1rm, sessions };
}

/* Running bests of one exercise. Shared by the live check and buildRecords
 * so the toast and the PR wall can never disagree on what a PR is. */
function newTracker(assisted) {
  return {
    assisted,
    top: null,            // heaviest set (assisted: lightest), most reps at that weight
    best: null,           // best e1RM set
    repsAt: new Map(),    // weight -> most reps
    minAt: new Map(),     // reps -> lowest weight (assisted only)
  };
}

function track(t, s) {
  const { weight, reps } = s;
  if (!t.top || (t.assisted ? weight < t.top.weight : weight > t.top.weight)) {
    t.top = { weight, reps };
  } else if (weight === t.top.weight && reps > t.top.reps) {
    t.top = { weight, reps };
  }
  if (!t.assisted) {
    const e = epley(weight, reps);
    if (e !== null && (!t.best || e > t.best.e1rm || (e === t.best.e1rm && weight > t.best.weight))) {
      t.best = { weight, reps, e1rm: e };
    }
  }
  if (!t.repsAt.has(weight) || reps > t.repsAt.get(weight)) t.repsAt.set(weight, reps);
  if (t.assisted && (!t.minAt.has(reps) || weight < t.minAt.get(reps))) t.minAt.set(reps, weight);
}

/* Lowest counterweight ever moved for at least `reps` reps. */
function assistedRecord(t, reps) {
  let rec = null;
  t.minAt.forEach((w, r) => {
    if (r < reps) return;
    if (!rec || w < rec.weight || (w === rec.weight && r > rec.reps)) rec = { weight: w, reps: r };
  });
  return rec;
}

function asPrev(set, assisted) {
  return { weight: set.weight, reps: set.reps, e1rm: assisted ? null : epley(set.weight, set.reps) };
}

/* Which PR types one set earns against the tracker, with the record each
 * type beat. Nothing is earned against an empty history. */
function evaluate(t, weight, reps, minGain) {
  const e1rm = t.assisted ? null : epley(weight, reps);
  const hits = {};
  if (t.assisted) {
    const rec = assistedRecord(t, reps);
    if (rec && weight < rec.weight) hits.weight = asPrev(rec, true);
  } else if (t.top && weight > t.top.weight) {
    hits.weight = asPrev(t.top, false);
  }
  if (e1rm !== null && t.best && round1(e1rm - t.best.e1rm) >= minGain) {
    hits.e1rm = { weight: t.best.weight, reps: t.best.reps, e1rm: t.best.e1rm };
  }
  if (t.repsAt.has(weight) && reps > t.repsAt.get(weight)) {
    hits.reps = asPrev({ weight, reps: t.repsAt.get(weight) }, t.assisted);
  }
  const e1rmGain = e1rm !== null && t.best ? round1(e1rm - t.best.e1rm) : null;
  return { hits, e1rm, e1rmGain };
}

function makeEvent(date, exercise, types, weight, reps, ev) {
  return {
    date,
    exercise,
    type: types[0],
    types,
    set: { weight, reps },
    e1rm: ev.e1rm,
    prev: ev.hits[types[0]],
    e1rmGain: ev.e1rmGain,
  };
}

/**
 * Live PR check for one set (the Today toast). Compares against every date
 * before `date` plus the other done sets of `date`, so re-ticking or editing
 * a set never compares it with itself.
 * @param {object} logs state.logs
 * @param {string} name exercise name
 * @param {string} date YYYY-MM-DD of the set
 * @param {number} weight
 * @param {number} reps
 * @param {{setIndex?: number, assisted?: boolean, exerciseMeta?: object,
 *   baselineSessions?: number, minE1rmGain?: number}} [opts]
 *   setIndex: the set's own index on `date`, left out of the comparison;
 *   baselineSessions (default 1): earlier sessions needed, so an exercise's
 *   first session never fires; minE1rmGain default 0.5 kg
 * @returns {null|{date, exercise, type:'weight'|'e1rm'|'reps', types:string[],
 *   set:{weight, reps}, e1rm:number|null, prev:{weight, reps, e1rm}, e1rmGain:number|null}}
 *   type is the headline (weight > e1rm > reps); types lists all earned
 */
export function checkSetPr(logs, name, date, weight, reps, opts = {}) {
  const w = num(weight);
  const r = num(reps);
  if (!(w > 0 && r > 0) || typeof date !== 'string') return null;
  const assisted = resolveAssisted(name, opts);
  const baseline = optNum(opts, 'baselineSessions', 1);
  const minGain = optNum(opts, 'minE1rmGain', 0.5);
  const skip = opts && typeof opts.setIndex === 'number' ? opts.setIndex : null;

  const t = newTracker(assisted);
  let sessions = 0;
  for (const k of dateKeys(logs)) {
    if (k > date) break;
    const sets = completedSets(setsOf(logs, k, name));
    if (k === date) {
      sets.filter(s => s.index !== skip).forEach(s => track(t, s));
    } else if (sets.length) {
      sessions += 1;
      sets.forEach(s => track(t, s));
    }
  }
  if (sessions < baseline) return null;

  const ev = evaluate(t, w, r, minGain);
  const types = TYPE_ORDER.filter(type => ev.hits[type]);
  return types.length ? makeEvent(date, name, types, w, r, ev) : null;
}

/* The set a session is credited with for each PR type: heaviest (assisted:
 * lightest) for weight and reps, highest e1RM for e1rm. */
function pickSessionPrs(t, sets, minGain) {
  const chosen = {};
  for (const s of sets) {
    const ev = evaluate(t, s.weight, s.reps, minGain);
    for (const type of TYPE_ORDER) {
      if (!ev.hits[type]) continue;
      const cur = chosen[type];
      let better;
      if (!cur) better = true;
      else if (type === 'e1rm') better = ev.e1rm > cur.ev.e1rm || (ev.e1rm === cur.ev.e1rm && s.weight > cur.s.weight);
      else if (s.weight !== cur.s.weight) better = t.assisted ? s.weight < cur.s.weight : s.weight > cur.s.weight;
      else better = s.reps > cur.s.reps;
      if (better) chosen[type] = { s, ev };
    }
  }
  return chosen;
}

/**
 * PR history and records table from all logs, oldest first.
 * @param {object} logs state.logs
 * @param {{exerciseMeta?: object, baselineSessions?: number, minE1rmGain?: number,
 *   today?: string, staleDays?: number, staleSessions?: number}} [opts]
 *   baselineSessions (default 3): an exercise's first sessions set the
 *   records without emitting events; minE1rmGain default 0.5 kg; today only
 *   feeds daysSinceLastPr and stale (default thresholds 28 days and 3 sessions)
 * @returns {{
 *   events: Array<{date, exercise, type, types, set:{weight, reps}, e1rm, prev:{weight, reps, e1rm}, e1rmGain}>,
 *   table: Array<{exercise, assisted, sessions, firstDate, lastDate,
 *     bestE1rm:{e1rm, weight, reps, date}|null, bestSet:{weight, reps, date},
 *     bestVolume:{volume, date}, prCount, lastPrDate:string|null,
 *     sessionsSinceLastPr, daysSinceLastPr:number|null, stale:boolean|null}>
 * }}
 *   events ascending by date, then exercise, then type; at most one event
 *   per set per session (types merged), table sorted by exercise name
 */
export function buildRecords(logs, opts = {}) {
  const meta = opts && opts.exerciseMeta;
  const baseline = optNum(opts, 'baselineSessions', 3);
  const minGain = optNum(opts, 'minE1rmGain', 0.5);
  const staleDays = optNum(opts, 'staleDays', 28);
  const staleSessions = optNum(opts, 'staleSessions', 3);
  const today = opts && dayNumber(opts.today) !== null ? opts.today : null;

  const rows = {};
  const events = [];
  for (const date of dateKeys(logs)) {
    const day = logs[date];
    if (!day || typeof day !== 'object' || Array.isArray(day)) continue;
    for (const name of Object.keys(day).sort()) {
      const sets = completedSets(day[name]);
      if (!sets.length) continue;
      if (!rows[name]) {
        const assisted = isAssisted(name, meta);
        rows[name] = {
          t: newTracker(assisted), assisted, sessions: 0, firstDate: date, lastDate: date,
          bestE1rm: null, bestSet: null, bestVolume: null, prCount: 0, lastPrDate: null,
          lastPrSession: null, baselineEnd: null,
        };
      }
      const row = rows[name];

      if (row.sessions >= baseline) {
        const chosen = pickSessionPrs(row.t, sets, minGain);
        const groups = [];
        for (const type of TYPE_ORDER) {
          if (!chosen[type]) continue;
          const { s, ev } = chosen[type];
          const same = groups.find(g => g.s.weight === s.weight && g.s.reps === s.reps);
          if (same) same.types.push(type);
          else groups.push({ s, ev, types: [type] });
        }
        if (groups.length) {
          groups.forEach(g => events.push(makeEvent(date, name, g.types, g.s.weight, g.s.reps, g.ev)));
          row.prCount += groups.length;
          row.lastPrDate = date;
          row.lastPrSession = row.sessions;
        }
      }

      let volume = 0;
      for (const s of sets) {
        track(row.t, s);
        volume += s.weight * s.reps;
        const e = row.assisted ? null : epley(s.weight, s.reps);
        if (e !== null && (!row.bestE1rm || e > row.bestE1rm.e1rm)) {
          row.bestE1rm = { e1rm: e, weight: s.weight, reps: s.reps, date };
        }
        const b = row.bestSet;
        const better = !b || (row.assisted ? s.weight < b.weight : s.weight > b.weight)
          || (s.weight === b.weight && s.reps > b.reps);
        if (better) {
          row.bestSet = { weight: s.weight, reps: s.reps, date };
        }
      }
      volume = round1(volume);
      if (!row.bestVolume || volume > row.bestVolume.volume) row.bestVolume = { volume, date };
      row.sessions += 1;
      row.lastDate = date;
      if (row.sessions === baseline) row.baselineEnd = date;
    }
  }

  const table = Object.keys(rows).sort().map(name => {
    const row = rows[name];
    // With no PR yet, the clock starts when the baseline ends: an exercise
    // that never beat its first sessions is as stuck as one with an old PR.
    const since = row.lastPrSession !== null
      ? row.sessions - row.lastPrSession - 1
      : Math.max(0, row.sessions - baseline);
    const ref = row.lastPrDate || row.baselineEnd || row.firstDate;
    const daysSinceLastPr = today !== null && row.lastPrDate ? dayNumber(today) - dayNumber(row.lastPrDate) : null;
    const stale = today !== null
      ? since >= staleSessions && dayNumber(today) - dayNumber(ref) >= staleDays
      : null;
    return {
      exercise: name,
      assisted: row.assisted,
      sessions: row.sessions,
      firstDate: row.firstDate,
      lastDate: row.lastDate,
      bestE1rm: row.bestE1rm,
      bestSet: row.bestSet,
      bestVolume: row.bestVolume,
      prCount: row.prCount,
      lastPrDate: row.lastPrDate,
      sessionsSinceLastPr: since,
      daysSinceLastPr,
      stale,
    };
  });
  return { events, table };
}
